import { defineMessages } from 'react-intl';

type MessageKeys =
	| 'newChatLabel'
	| 'agentListLabel'
	| 'createAgentLabel'
	| 'chatListLabel'
	| 'closeSideNavLabel'
	| 'giveFeedbackLabel'
	| 'pulseLabel'
	| 'tasksLabel'
	| 'spacesSectionLabel'
	| 'createSpaceLabel'
	| 'recommendedSpaces'
	| 'recommendedSpacesLabel';

const message: Record<MessageKeys, { id: string; defaultMessage: string; description?: string }> =
	defineMessages({
		newChatLabel: {
			id: 'ai-mate.rovo-navigation.side-navigation.new-chat',
			defaultMessage: 'New chat',
			description:
				'Button label displayed on the new chat button in the side navigation within the AI conversation interface. Shown as the text on a button. When clicked, it starts a new conversation session, clearing the current chat history. The purpose is to provide users with a clear way to begin a fresh conversation with the AI assistant from the side navigation.',
		},
		agentListLabel: {
			id: 'ai-mate.rovo-navigation.side-navigation.agent-list',
			defaultMessage: 'Agents',
			description:
				'Label text displayed as a section heading in the side navigation within the AI conversation interface, identifying the list of available AI agents. Shown as a section title or heading. The purpose is to identify the agents section, which displays the list of AI agents that users can select to chat with.',
		},
		createAgentLabel: {
			id: 'ai-mate.rovo-navigation.side-navigation.create-agent-label',
			defaultMessage: 'Create Agent',
			description:
				'Button label displayed on a button in the side navigation within the AI conversation interface that opens the agent creation interface. Shown as the text on a button. When clicked, it opens a dialog or form allowing users to create a new AI agent. The purpose is to provide users with a way to create custom AI agents from the side navigation.',
		},
		chatListLabel: {
			id: 'ai-mate.rovo-navigation.side-navigation.chat-list',
			defaultMessage: 'Chats',
			description:
				"Label text displayed as a section heading in the side navigation within the AI conversation interface, identifying the list of past chat conversations. Shown as a section title or heading. The purpose is to identify the chats section, which displays the user's conversation history.",
		},
		closeSideNavLabel: {
			id: 'ai-mate.rovo-navigation.side-navigation.close-side-nav',
			defaultMessage: 'Close',
			description:
				'Button label displayed on the close button in the side navigation within the AI conversation interface. Shown as the text on a button. When clicked, it closes or collapses the side navigation panel. The purpose is to provide users with a way to hide the side navigation to get more screen space for the main chat interface.',
		},
		giveFeedbackLabel: {
			id: 'ai-mate.rovo-navigation.side-navigation.give-feedback',
			defaultMessage: 'Give feedback',
			description:
				'Button label displayed on a feedback button in the side navigation within the AI conversation interface that opens a feedback form or dialog. Shown as the text on a button. When clicked, it opens a feedback interface allowing users to submit feedback about the AI assistant or the interface. The purpose is to provide users with a way to share their thoughts and help improve the product.',
		},
		pulseLabel: {
			id: 'ai-mate.rovo-navigation.side-navigation.pulse',
			defaultMessage: 'Insights',
			description: 'Button label for navigating to the list of insights.',
		},
		tasksLabel: {
			id: 'ai-mate.rovo-navigation.side-navigation.tasks',
			defaultMessage: 'Tasks',
			description: 'Button label for navigating to the tasks view in the Rovo side navigation.',
		},
		spacesSectionLabel: {
			id: 'ai-mate.rovo-navigation.side-navigation.spaces-section',
			defaultMessage: 'Rovo spaces',
			description:
				'Label for the expandable spaces section in Rovo chat global side navigation, shown next to the folder icon.',
		},
		createSpaceLabel: {
			id: 'ai-mate.rovo-navigation.side-navigation.create-rovo-space',
			defaultMessage: 'Create Rovo space',
			description:
				'Accessibility label for the add button next to the projects section in Rovo chat global side navigation.',
		},
		recommendedSpaces: {
			id: 'ai-mate.rovo-navigation.side-navigation.recommended-spaces',
			defaultMessage: 'Recommended Rovo spaces',
			description:
				'Button label for navigating to the recommended spaces view in the Rovo side navigation.',
		},
		recommendedSpacesLabel: {
			id: 'ai-mate.rovo-navigation.side-navigation.recommended-spaces-label',
			defaultMessage: 'Recommended Rovo spaces',
			description:
				'Accessibility label for the AI sparkle icon next to the recommended spaces button.',
		},
	});

export default message;
